import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { verifyMagicLink, getDisplayErrorMessage } from '@/lib/api';
import { isMfaPending, setMfaFlow } from '@/lib/mfaFlow';
import { TODAY_PATH, landsOnToday } from '@/lib/todayInbox';
import { takeReturnPath } from '@/lib/returnPath';
import { useAuthStore } from '@/store/auth';
import { AuthLayout } from '@/components/AuthLayout';
// Shared form classes via AAP.3's AuthForm extraction (was previously
// imported from LoginPage's module pre-AAP.3).
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

type VerifyResult = Awaited<ReturnType<typeof verifyMagicLink>>;

/**
 * Admin magic-link VERIFY page (AAP.2). Sibling of PWA's
 * MagicLinkVerifyPage. Reads `token` + `email` from the URL query
 * params (placed there by the email link AAP.1 templated against
 * ADMIN_ORIGIN), calls verifyMagicLink, populates the auth store,
 * routes to the role-appropriate landing.
 *
 * Role-aware landing
 * ──────────────────
 * Mirrors the logic at App.tsx:122 — fleet_admin → /fleets,
 * Site personas and everyone else →
 * /dashboard. Done inline here rather than relying on App.tsx's
 * post-login redirect effect because that effect only fires on
 * `path === '/login'`; this page lands on `/auth/magic-verify`,
 * so the inline routing avoids an extra render+redirect bounce.
 *
 * Google first-link notice
 * ────────────────────────
 * The Google callback appends ?notice=google_linked when it just
 * connected Google to a never-verified password account and rotated
 * that password (apps/api google.ts, account pre-hijack fix). The
 * exchange still runs first, but the session (or the MFA hop) is
 * completed only when the operator taps Continue, so the notice is
 * read before anything else happens. This page sits in the router's
 * signed-out branch; storing the session first would unmount it.
 *
 * Theme persistence
 * ─────────────────
 * Admin's design at this surface is deliberately NOT per-user
 * theme-applied — LoginPage's docstring (lines 28-43 pre-AAP.2-prep)
 * explicitly removed the setMode/setThemeId calls. The
 * ThemeProvider's default-dark fallback handles fresh sessions;
 * returning operators land in whatever mode they last selected via
 * the theme picker. So this page doesn't read or apply
 * active_theme_family / theme_mode (diverges from PWA's
 * MagicLinkVerifyPage which does — different product call).
 */
export function MagicLinkVerifyPage({ onNavigate }: Props) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [status, setStatus] = useState<'verifying' | 'linked' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const [pending, setPending] = useState<VerifyResult | null>(null);

  const complete = useCallback((res: VerifyResult) => {
    // MFA branch (dormant until commit 5 arms the server): the
    // pending shape routes to the enroll or challenge screen
    // with the 5-minute token held in in-memory flow state. The
    // normal session path below is unchanged.
    if (isMfaPending(res)) {
      setMfaFlow(res);
      onNavigate(res.variant === 'enroll' ? '/auth/mfa/enroll' : '/auth/mfa/challenge');
      return;
    }
    setAuth(res.user, res.access_token);
    // Role-aware landing, as App.tsx:122 does. A deep link the visitor
    // opened signed out (lib/returnPath.ts) wins: the reason they signed in.
    const landing =
      res.user.role === 'fleet_admin'
        ? '/fleets'
        // Warren, 21 October 2026, item M: the Super Admin starts on the Today inbox.
        : landsOnToday(res.user) ? TODAY_PATH : '/dashboard';
    onNavigate(takeReturnPath() ?? landing);
  }, [setAuth, onNavigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email');
    const token = params.get('token');
    const notice = params.get('notice');

    if (!email || !token) {
      setStatus('error');
      setErrorMsg('Missing email or token in the link');
      return;
    }

    verifyMagicLink(email, token)
      .then((res) => {
        if (notice === 'google_linked') {
          setPending(res);
          setStatus('linked');
          return;
        }
        complete(res);
      })
      .catch(async (err: unknown) => {
        setStatus('error');
        // Surface the server's structured message — notably the admin
        // gate's specific copy ("This account doesn't have admin
        // access") on the 403 a driver account gets here. Specific is
        // fine on this surface: whoever clicked the link already
        // controls the inbox. ky's raw HTTPError message is generic,
        // so the body-parsing helper does the extraction.
        setErrorMsg(await getDisplayErrorMessage(err, 'Invalid or expired link'));
      });
  }, [complete]);

  const title = status === 'verifying'
    ? 'Signing you in…'
    : status === 'linked'
      ? 'Google is now connected'
      : 'Sign-in failed';
  const subtitle = status === 'verifying'
    ? 'Verifying your magic link'
    : status === 'linked'
      ? 'One thing to know before you continue'
      : 'The link may have expired';

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      {status === 'verifying' ? (
        <div className={styles.spinnerWrap}>
          <Loader2 size={36} className={styles.spinner} />
        </div>
      ) : status === 'linked' ? (
        <div className={styles.errorStack}>
          <div role="status" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              You can sign in with Google from now on. The password that was on
              this account no longer works. If you want a password too, use
              &ldquo;Forgot password&rdquo; on the sign-in screen to set a new one.
              We&rsquo;ve emailed you this notice as well.
            </span>
          </div>
          <button
            type="button"
            className={formStyles.submitBtn}
            onClick={() => { if (pending) complete(pending); }}
          >
            Continue
          </button>
        </div>
      ) : (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {errorMsg}
          </div>
          <button
            type="button"
            className={formStyles.submitBtn}
            onClick={() => onNavigate('/auth/magic')}
          >
            Request a new link
          </button>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => onNavigate('/login')}
          >
            Use password instead
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
